/**
 * Meta Ads PDP loader
 * Detects Meta Ads URLs client-side, fetches product.meta-ad template HTML,
 * extracts .meta-ad-pdp-main-wrapper, and swaps it into the live page.
 */
(function (window, document) {
  'use strict';

  if (window.metaAdPdpLoader) return;

  var HTML_CACHE_PREFIX = 'metaAdPdpHtml:';
  var VIEW_PARAM = 'meta-ad';
  var state = {
    detection: null,
    loading: false,
    loaded: false,
    prefetchPromise: null,
  };

  function getSessionApi() {
    return window.metaPdpSession || null;
  }

  function getProductHandle() {
    return (window.Shopify && window.Shopify.product_handle) || '';
  }

  function getRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  /**
   * Same conditions as metaPdpSession.isMetaAdUrl + session continuation.
   * Result is cached for the page lifetime.
   */
  function shouldLoadMetaAdPdp() {
    if (state.detection !== null) return state.detection;

    var sessionApi = getSessionApi();
    if (!sessionApi) {
      return false;
    }

    if (!sessionApi.isMobileViewport()) {
      state.detection = false;
      return false;
    }

    var isUrl = typeof sessionApi.isMetaAdUrl === 'function' && sessionApi.isMetaAdUrl();
    if (isUrl) {
      state.detection = true;
      return true;
    }

    var handle = getProductHandle();
    // Don't cache a negative result before product handle is available
    if (!handle) return false;

    var session = typeof sessionApi.read === 'function' ? sessionApi.read() : null;
    var isContinuation =
      !!(session && typeof sessionApi.isHandleInSession === 'function' &&
        sessionApi.isHandleInSession(handle, session));

    state.detection = isContinuation;
    return state.detection;
  }

  function getFetchUrl() {
    var handle = getProductHandle();
    if (!handle) return '';
    return getRoot() + 'products/' + encodeURIComponent(handle) + '?view=' + VIEW_PARAM;
  }

  function getHtmlCacheKey() {
    return HTML_CACHE_PREFIX + getProductHandle();
  }

  function readHtmlCache() {
    try {
      return sessionStorage.getItem(getHtmlCacheKey());
    } catch (e) {
      return null;
    }
  }

  function writeHtmlCache(html) {
    if (!html) return;
    try {
      sessionStorage.setItem(getHtmlCacheKey(), html);
    } catch (e) {
      /* ignore quota / private mode */
    }
  }

  function markPending(isPending) {
    var root = document.documentElement;
    if (!root) return;
    if (isPending) {
      root.classList.add('meta-ad-pdp-pending');
      if (document.body) {
        document.body.classList.add('meta-ad-api-loading');
      }
    } else {
      root.classList.remove('meta-ad-pdp-pending');
    }
  }

  function extractMetaWrapper(html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('.meta-ad-pdp-main-wrapper');
  }

  /**
   * Re-execute scripts after DOMParser import (scripts from parseFromString do not run).
   */
  function executeScripts(container) {
    if (!container) return;
    var scripts = container.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var oldScript = scripts[i];
      var newScript = document.createElement('script');
      for (var a = 0; a < oldScript.attributes.length; a++) {
        var attr = oldScript.attributes[a];
        newScript.setAttribute(attr.name, attr.value);
      }
      if (!oldScript.src) {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    }
  }

  function getMountTarget() {
    return (
      document.querySelector('.meta-ad-pdp-main-wrapper[data-meta-ad-mount="true"]') ||
      document.querySelector('.meta-ad-pdp-main-wrapper')
    );
  }

  function removeStandardPdpDom() {
    var mainContent = document.getElementById('MainContent');
    if (!mainContent) return;

    var keep = mainContent.querySelector('.meta-ad-pdp-main-wrapper[data-meta-ad-content="true"]');
    if (!keep) {
      keep = mainContent.querySelector('.meta-ad-pdp-wrapper[data-meta-ad="true"]');
      if (keep) keep = keep.closest('.meta-ad-pdp-main-wrapper') || keep;
    }
    if (!keep) return;

    var sections = mainContent.querySelectorAll(':scope > [id^="shopify-section-"]');
    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (section.contains(keep)) continue;
      section.remove();
    }

    var standard = mainContent.querySelectorAll(
      '.hdt-main-product[data-standard-pdp="true"], .hdt-sticky-btn-atc, [data-meta-ad-mount="true"]'
    );
    for (var j = 0; j < standard.length; j++) {
      if (keep.contains(standard[j])) continue;
      standard[j].remove();
    }

    var hostSection = keep.closest('[id^="shopify-section-"]');
    if (hostSection) {
      var siblings = hostSection.children;
      for (var k = siblings.length - 1; k >= 0; k--) {
        var child = siblings[k];
        if (child === keep || keep.contains(child) || child.contains(keep)) continue;
        if (child.id === 'meta-pdp-variant-family-data') continue;
        if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') {
          if (child.id === 'meta-pdp-variant-family-data') continue;
          if (child.getAttribute('type') === 'application/json') continue;
        }
        if (child.classList && child.classList.contains('hdt-main-product')) {
          child.remove();
        }
      }
    }
  }

  function restoreOnFallback() {
    markPending(false);
    if (document.body) {
      document.body.classList.remove('meta-ad-api-loading');
    }

    var injected = document.querySelector('.meta-ad-pdp-main-wrapper[data-meta-ad-content="true"]');
    if (injected) injected.remove();

    var standardNodes = document.querySelectorAll(
      '.hdt-main-product[data-standard-pdp="true"], .hdt-main-product, .hdt-sticky-btn-atc'
    );
    for (var i = 0; i < standardNodes.length; i++) {
      standardNodes[i].style.display = '';
      standardNodes[i].style.visibility = '';
      standardNodes[i].style.opacity = '';
      standardNodes[i].style.height = '';
      standardNodes[i].style.overflow = '';
    }

    var mount = getMountTarget();
    if (!mount) {
      var host =
        document.querySelector('.hdt-main-section--product') ||
        document.querySelector('[id^="shopify-section-"] .hdt-main-product');
      if (host) {
        var section = host.closest('[id^="shopify-section-"]') || host.parentNode;
        mount = document.createElement('div');
        mount.className = 'meta-ad-pdp-main-wrapper';
        mount.setAttribute('data-meta-ad-mount', 'true');
        if (section) section.insertBefore(mount, section.firstChild);
      }
    }

    state.loaded = false;
    state.loading = false;
  }

  function swapContent(sourceWrapper) {
    if (!sourceWrapper) return false;

    var mount = getMountTarget();
    if (!mount) return false;

    var imported = document.importNode(sourceWrapper, true);
    imported.setAttribute('data-meta-ad-content', 'true');
    imported.removeAttribute('data-meta-ad-mount');

    mount.replaceWith(imported);
    executeScripts(imported);

    var existingFamily = document.getElementById('meta-pdp-variant-family-data');
    var importedFamily = imported.querySelector('#meta-pdp-variant-family-data');
    if (importedFamily && existingFamily && existingFamily !== importedFamily) {
      existingFamily.remove();
    }

    // Hide standard PDP immediately; keep loading overlay until API success
    var standardNodes = document.querySelectorAll(
      '.hdt-main-product[data-standard-pdp="true"], .hdt-sticky-btn-atc'
    );
    for (var s = 0; s < standardNodes.length; s++) {
      standardNodes[s].style.display = 'none';
    }

    state.loaded = true;
    // Reveal MainContent shell but keep overlay via meta-ad-api-loading
    if (document.documentElement) {
      document.documentElement.classList.remove('meta-ad-pdp-pending');
    }
    if (document.body) {
      document.body.classList.add('meta-ad-api-loading');
    }

    window.dispatchEvent(
      new CustomEvent('metaAdPdpContentLoaded', {
        detail: { handle: getProductHandle() },
      })
    );

    return true;
  }

  function fetchHtml() {
    if (state.prefetchPromise) return state.prefetchPromise;

    var cached = readHtmlCache();
    if (cached) {
      state.prefetchPromise = Promise.resolve(cached);
      return state.prefetchPromise;
    }

    var url = getFetchUrl();
    if (!url) {
      return Promise.reject(new Error('Meta Ad PDP: missing product handle'));
    }

    state.prefetchPromise = fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store',
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Meta Ad PDP: fetch failed (' + response.status + ')');
      }
      return response.text();
    }).then(function (html) {
      var wrapper = extractMetaWrapper(html);
      if (wrapper) {
        writeHtmlCache(wrapper.outerHTML);
      }
      return html;
    });

    return state.prefetchPromise;
  }

  function resolveWrapperHtml(html) {
    // Cached value may be the wrapper fragment or a full document
    var wrapper = extractMetaWrapper(html);
    if (wrapper) return wrapper;
    // If cache stored only the wrapper, parseFromString still wraps it in html/body
    return null;
  }

  function loadAndSwap() {
    if (state.loaded) return Promise.resolve(true);
    if (state.loading) {
      return state.prefetchPromise
        ? state.prefetchPromise.then(function () {
            return state.loaded;
          })
        : Promise.resolve(false);
    }

    if (!shouldLoadMetaAdPdp()) {
      return Promise.resolve(false);
    }

    state.loading = true;
    markPending(true);

    return fetchHtml()
      .then(function (html) {
        var wrapper = resolveWrapperHtml(html);
        if (!wrapper) {
          throw new Error('Meta Ad PDP: .meta-ad-pdp-main-wrapper not found');
        }

        // Attach before executeScripts — API cache path can succeed synchronously
        window.addEventListener(
          'metaAdApiLoaded',
          function () {
            removeStandardPdpDom();
          },
          { once: true }
        );
        window.addEventListener(
          'metaAdApiError',
          function () {
            restoreOnFallback();
          },
          { once: true }
        );
        window.addEventListener(
          'metaAdApiFallback',
          function () {
            restoreOnFallback();
          },
          { once: true }
        );

        var ok = swapContent(wrapper);
        if (!ok) {
          throw new Error('Meta Ad PDP: mount target missing');
        }

        if (document.body && document.body.classList.contains('meta-ad-api-loaded')) {
          removeStandardPdpDom();
        }

        state.loading = false;
        return true;
      })
      .catch(function (err) {
        console.error(err);
        state.loading = false;
        state.prefetchPromise = null;
        restoreOnFallback();
        if (window.metaAdData) window.metaAdData.isMetaAd = false;
        markPending(false);
        return false;
      });
  }

  /**
   * Start prefetch as early as possible (safe to call from <head>).
   */
  function prefetch() {
    if (!shouldLoadMetaAdPdp()) return null;
    if (state.loaded) return null;
    markPending(true);
    return fetchHtml().catch(function (err) {
      console.error(err);
      state.prefetchPromise = null;
      return null;
    });
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function init() {
    if (!shouldLoadMetaAdPdp()) return;

    prefetch();

    onReady(function () {
      loadAndSwap();
    });
  }

  window.metaAdPdpLoader = {
    shouldLoad: shouldLoadMetaAdPdp,
    prefetch: prefetch,
    load: loadAndSwap,
    init: init,
    isLoaded: function () {
      return state.loaded;
    },
    getDetection: function () {
      return state.detection;
    },
  };

  init();
})(window, document);
